import { ForwardRefExoticComponent, RefAttributes } from "react";
import {
  Archive,
  Globe,
  LayoutDashboard,
  LucideProps,
  UsersRound,
  Inbox,
  PlusCircle,
  ShieldCheck,
  Shield,
  BookOpen,
  ShieldAlert,
  ClipboardList,
  Eye,
  FolderSearch,
  Settings2,
  Building2,
  FileText,
} from "lucide-react";
import {
  Menu,
  MenuItem,
  MenuOption,
  MenuOptionsGroup,
} from "core/dashboard/menu/menu";
import DashboardNavigationLocations from "core/navigation/DashboardNavigationLocations";
import PublicNavigationLocations from "core/navigation/PublicNavigationLocations";
import MapBrowserPath from "details/navigation/browser/mappers/mapBrowserPath/MapBrowserPath";
import { StringsKeys } from "@/core/internationalization/strings";

type Icon =
  | ForwardRefExoticComponent<
      Omit<LucideProps, "ref"> & RefAttributes<SVGSVGElement>
    >
  | undefined;

export interface DashboardMenuOption extends Omit<MenuOption, "icon"> {
  Icon?: Icon;
  path: string;
}

export interface DashboardMenuOptionsGroup extends Omit<
  MenuOptionsGroup,
  "icon" | "children"
> {
  Icon?: Icon;
  children: DashboardMenuItem[];
}

export type DashboardMenuItem = DashboardMenuOption | DashboardMenuOptionsGroup;

export default class MapDashboardMenuService {
  constructor(private readonly menu: Menu) {}

  private getLocationPath(
    location: DashboardNavigationLocations | PublicNavigationLocations,
  ): string {
    return MapBrowserPath.mapToBrowserPath(location);
  }

  private getLocationIcon(location: string): Icon | undefined {
    switch (location) {
      case DashboardNavigationLocations.DASHBOARD:
        return LayoutDashboard;
      case DashboardNavigationLocations.ROLES_AND_RIGHTS:
        return Shield;
      case DashboardNavigationLocations.RISK_ENTITY_REFERENTIAL:
        return BookOpen;
      case DashboardNavigationLocations.NOUVELLE_DEMANDE:
        return PlusCircle;
      case DashboardNavigationLocations.COMPLIANCE_L1_REVIEW:
        return ClipboardList;
      case DashboardNavigationLocations.COMPLIANCE_P4_VALIDATION:
        return Eye;
      case DashboardNavigationLocations.KYB_ACCESS_RECERTIFICATION:
        return ClipboardList;
      case DashboardNavigationLocations.KYB_MONITORING:
        return LayoutDashboard;
      case DashboardNavigationLocations.KYB_COMPLEMENT_DOCUMENTS:
        return FileText;
      case DashboardNavigationLocations.KYB_RETENTION:
        return Archive;
      case DashboardNavigationLocations.KYB_PERIMETER:
        return Globe;
      case DashboardNavigationLocations.COMPLIANCE_EDD:
        return FolderSearch;
      case DashboardNavigationLocations.PLATFORM_SETTINGS:
        return Settings2;
      case DashboardNavigationLocations.AGENCES:
        return Building2;
      default:
        return undefined;
    }
  }

  private getMenuOptionsGroupIcon(title: string): Icon | undefined {
    switch (title) {
      case StringsKeys.dashboard:
        return LayoutDashboard;
      case StringsKeys.customers:
        return UsersRound;
      case StringsKeys.demandes:
        return Inbox;
      case StringsKeys.administration:
        return ShieldCheck;
      case StringsKeys.risquesConformite:
        return ShieldAlert;
      case StringsKeys.kybMenuGroup:
        return Building2;
      default:
        return undefined;
    }
  }

  private mapMenuOption(menuOption: MenuItem): DashboardMenuItem {
    let Icon: Icon;
    let path: string | undefined;

    if ((menuOption as MenuOption).location) {
      Icon = this.getLocationIcon((menuOption as MenuOption).location);
      path = this.getLocationPath((menuOption as MenuOption).location);
    } else if ((menuOption as MenuOptionsGroup).children) {
      Icon = this.getMenuOptionsGroupIcon(
        (menuOption as MenuOptionsGroup).title,
      );
      (menuOption as MenuOptionsGroup).children = (
        menuOption as MenuOptionsGroup
      ).children.map(this.mapMenuOption.bind(this));
    }

    const result = {
      ...menuOption,
      ...(Icon && { Icon }),
      ...(path && { path }),
    } as DashboardMenuItem;
    return result;
  }

  map(): DashboardMenuItem[] {
    const mappedMenu = this.menu.map(this.mapMenuOption.bind(this));
    return mappedMenu;
  }
}
